import React from 'react';
import { Alert, Button, ButtonGroup, Heading, Modal, Select, Table, TextButton } from '@zillow/constellation-10';
import { numberUtils, useNodeEnv, useRentalManagerUser, useRmwPaths, useRouter } from '@zg-rentals/rental-manager-env';
import billingUtils from '../../utils/billingUtils';
import SpinnerContainer from '../../components/SpinnerContainer';
import { useCreateMultiPayment } from '../../hooks/billingPortal';
import type {
  AdproductsUserMetroidAlicornPaymentResultDto,
  MultiPaymentRequestDto,
  UserZuoraAccountPaymentMethodListDto,
  UserZuoraPaymentMethodDto,
} from '@zg-rentals/ts-adproducts-client';
import {
  AdproductsUserBillingInvoicePaymentApiHandler_getPaymentMethodsForAccount,
  type AdproductsZuoraBillingInvoiceDto,
} from '@zg-rentals/ts-adproducts-client';
const formatPaymentMethod = (paymentMethod: UserZuoraPaymentMethodDto) => {
  if (paymentMethod.paymentType === 'CreditCard') {
    return `${paymentMethod.brand} ending with 
        ${billingUtils.getCreditCardLastDigits(paymentMethod.maskNumber)} `;
  }
  if (paymentMethod.paymentType === 'ACH') {
    return `${paymentMethod.bankName} ending with 
        ${billingUtils.getCreditCardLastDigits(paymentMethod.maskNumber)} `;
  }
  return 'Unknown Payment Method';
};

const PAYMENT_STATES = {
  INITIAL: 'initial',
  LOADING_PAYMENT_METHODS: 'loadingPaymentMethods',
  FAILED_LOADING_PAYMENT_METHODS: 'failedLoadingPaymentMethods',
  PAYMENT_METHODS_READY: 'paymentMethodsReady',
  MAKING_PAYMENT: 'makingPayment',
  FAILED_INITIALIZING_PAYMENT: 'failedInitializingPaymnent',
  PAYMENT_COMPLETE: 'paymentComplete',
};

type MultiPaymentAccountSummary = {
  accountName: string;
  count: number;
  accountNumber: string;
  balance: number;
  taxAmount: number;
};

type AccountMultiSummary = Record<string, MultiPaymentAccountSummary>;

export type MultiPaymentSummary = Record<string, MultiPaymentRequestDto>;

export type MultiInvoicePayModalProps = {
  open: boolean;
  close: () => void;
  data: Array<AdproductsZuoraBillingInvoiceDto>;
  selected: Set<number>;
  deselectAll: () => void;
  refreshInvoices: () => void;
};

const MultiInvoicePayModal = ({
  open,
  close,
  data,
  selected,
  deselectAll,
  refreshInvoices,
}: MultiInvoicePayModalProps) => {
  const [error, setError] = React.useState<string | null>(null);
  const paths = useRmwPaths();
  const router = useRouter();
  const [selectedPaymentMethods, setSelectedPaymentMethods] = React.useState<Record<string, string>>({});
  const [availablePaymentMethods, setAvailablePaymentMethods] = React.useState<
    Record<string, UserZuoraAccountPaymentMethodListDto>
  >({});
  const [paymentState, setPaymentState] = React.useState(PAYMENT_STATES.INITIAL);
  const [results, setResults] = React.useState<Array<AdproductsUserMetroidAlicornPaymentResultDto>>([]);

  const { user } = useRentalManagerUser();
  const environment = useNodeEnv();
  const { impersonatorEmail, impersonatorUserToken } = user;
  const { isProduction } = environment;
  const disableImpersonatorPayments = isProduction && (impersonatorEmail != null || impersonatorUserToken != null);

  const selectedInvoices: Array<AdproductsZuoraBillingInvoiceDto> = React.useMemo(() => {
    if (data.length === 0) return [];
    return data.filter((item, i) => selected.has(i));
  }, [selected, data]);

  const selectionMap: AccountMultiSummary = React.useMemo(() => {
    return selectedInvoices.reduce((a: AccountMultiSummary, b: AdproductsZuoraBillingInvoiceDto) => {
      a[b.accountNumber] = a[b.accountNumber] || {
        accountName: b.accountName,
        count: 0,
        accountNumber: b.accountNumber,
        balance: 0,
        taxAmount: 0,
      };
      a[b.accountNumber].count += 1;
      a[b.accountNumber].balance += b.balance;
      a[b.accountNumber].taxAmount += b.taxAmount;
      return a;
    }, {});
  }, [selectedInvoices]);

  const multiPayment = React.useMemo(() => {
    return selectedInvoices.reduce((a: MultiPaymentSummary, b: AdproductsZuoraBillingInvoiceDto) => {
      a[b.accountNumber] = a[b.accountNumber] || {
        paymentMethodId: selectedPaymentMethods[b.accountNumber],
        invoices: [],
      };
      a[b.accountNumber].invoices.push(b.invoiceNumber);
      return a;
    }, {});
  }, [selectedInvoices, selectedPaymentMethods]);

  const { mutateAsync } = useCreateMultiPayment(multiPayment);

  const resetState = () => {
    setSelectedPaymentMethods({});
    setPaymentState(PAYMENT_STATES.INITIAL);
    setError(null);
    setResults([]);
  };

  const closeModal = () => {
    resetState();
    close();
  };

  React.useEffect(() => {
    setPaymentState(PAYMENT_STATES.LOADING_PAYMENT_METHODS);
    const accountNumbers = Object.keys(selectionMap);
    if (accountNumbers.length === 0) {
      return;
    }
    const requests = accountNumbers.map((accountNumber) =>
      AdproductsUserBillingInvoicePaymentApiHandler_getPaymentMethodsForAccount({ accountNumber }),
    );
    Promise.all(requests)
      .then((res) => {
        const reducer: Record<string, UserZuoraAccountPaymentMethodListDto> = {};
        const paymentMethodMap = res.reduce((a, b) => {
          a[b.accountNumber] = b;
          return a;
        }, reducer);
        setAvailablePaymentMethods(paymentMethodMap);
        setPaymentState(PAYMENT_STATES.PAYMENT_METHODS_READY);
        return;
      })
      .catch(() => {
        setError('Failed to load payment methods, please try again later');
        setPaymentState(PAYMENT_STATES.FAILED_LOADING_PAYMENT_METHODS);
      });
  }, [selectionMap]);

  const handleEditClick = (accountNumber: string) => {
    router.push(`${paths.billingPage}/account/${accountNumber}?prev=due`);
  };

  const mapPaymentMethods = (account: MultiPaymentAccountSummary) => {
    const { accountNumber } = account;
    if (!availablePaymentMethods[accountNumber].paymentMethods.length) {
      return <TextButton onClick={() => handleEditClick(accountNumber)}>Setup payment method</TextButton>;
    }
    return (
      <Select
        disabled={paymentState === PAYMENT_STATES.MAKING_PAYMENT}
        onChange={(e: React.ChangeEvent<HTMLSelectElement>) => {
          setSelectedPaymentMethods({
            ...selectedPaymentMethods,
            [accountNumber]: e.target.value,
          });
        }}
        value={selectedPaymentMethods[accountNumber] || 'default'}
      >
        <option value="default" disabled={true} hidden={true}>
          Choose a payment method
        </option>
        {availablePaymentMethods[accountNumber].paymentMethods.map((paymentMethod) => (
          <option key={paymentMethod.paymentMethodId} value={paymentMethod.paymentMethodId}>
            {formatPaymentMethod(paymentMethod)}
          </option>
        ))}
      </Select>
    );
  };

  const renderMultiPaymentTable = () => {
    if (error) {
      return <Alert tone="critical">{error}</Alert>;
    }
    if (paymentState === PAYMENT_STATES.LOADING_PAYMENT_METHODS || !Object.keys(availablePaymentMethods).length) {
      return <SpinnerContainer />;
    }
    const grandTotal = Object.values(selectionMap).reduce((a, b) => a + b.balance, 0);
    const grandTotalTax = Object.values(selectionMap).reduce((a, b) => a + b.taxAmount, 0);
    const showTaxColumns = grandTotalTax > 0;
    const totalColumnLabel = showTaxColumns ? 'Total after taxes' : 'Total';
    return (
      <Table.Responsive>
        <Table.Root aria-label="multi payment table" appearance="bare">
          <Table.Header>
            <Table.Row>
              <Table.HeaderCell>Invoices</Table.HeaderCell>
              <Table.HeaderCell>Account</Table.HeaderCell>
              {showTaxColumns && <Table.HeaderCell>Subtotal</Table.HeaderCell>}
              {showTaxColumns && <Table.HeaderCell>Tax</Table.HeaderCell>}
              <Table.HeaderCell>{totalColumnLabel}</Table.HeaderCell>
              <Table.HeaderCell>Payment Method</Table.HeaderCell>
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {Object.values(selectionMap).map((account) => (
              <Table.Row key={account.accountNumber}>
                <Table.Cell>{account.count}</Table.Cell>
                <Table.Cell>{account.accountName}</Table.Cell>
                {showTaxColumns && (
                  <Table.Cell>{numberUtils.asCurrency(account.balance - account.taxAmount)}</Table.Cell>
                )}
                {showTaxColumns && <Table.Cell>{numberUtils.asCurrency(account.taxAmount)}</Table.Cell>}
                <Table.Cell>{numberUtils.asCurrency(account.balance)}</Table.Cell>
                <Table.Cell>{mapPaymentMethods(account)}</Table.Cell>
              </Table.Row>
            ))}
            <Table.Row>
              <Table.HeaderCell css={{ fontWeight: 'bold' }}>Grand Total</Table.HeaderCell>
              <Table.HeaderCell>&nbsp;</Table.HeaderCell>
              {showTaxColumns && <Table.HeaderCell>&nbsp;</Table.HeaderCell>}
              {showTaxColumns && <Table.HeaderCell>&nbsp;</Table.HeaderCell>}
              <Table.HeaderCell css={{ fontWeight: 'bold' }}>{numberUtils.asCurrency(grandTotal)}</Table.HeaderCell>
              <Table.HeaderCell>&nbsp;</Table.HeaderCell>
            </Table.Row>
          </Table.Body>
        </Table.Root>
      </Table.Responsive>
    );
  };

  const handleMakePayment = () => {
    setPaymentState(PAYMENT_STATES.MAKING_PAYMENT);
    void mutateAsync()
      .then((results: Array<AdproductsUserMetroidAlicornPaymentResultDto>) => {
        setResults(results);
        setPaymentState(PAYMENT_STATES.PAYMENT_COMPLETE);
        return;
      })
      .catch(() => {
        setError('Failed to initialize payments, please try again later');
        setPaymentState(PAYMENT_STATES.INITIAL);
      });
  };

  const closeAndRefreshInvoices = () => {
    deselectAll();
    refreshInvoices();
    closeModal();
  };

  const renderFooter = () => {
    if (paymentState === PAYMENT_STATES.PAYMENT_COMPLETE) {
      return (
        <Button tone="brand" emphasis="filled" onClick={closeAndRefreshInvoices}>
          Close
        </Button>
      );
    }
    return (
      <ButtonGroup aria-label="Modal actions">
        <Button
          tone="neutral"
          emphasis="filled"
          disabled={paymentState === PAYMENT_STATES.MAKING_PAYMENT}
          onClick={closeModal}
        >
          Cancel
        </Button>
        <Button
          tone="brand"
          emphasis="filled"
          onClick={handleMakePayment}
          disabled={
            paymentState === PAYMENT_STATES.MAKING_PAYMENT ||
            Object.values(selectedPaymentMethods).length < Object.values(selectionMap).length ||
            disableImpersonatorPayments
          }
        >
          {paymentState === PAYMENT_STATES.MAKING_PAYMENT ? 'Loading...' : 'Pay'}
        </Button>
      </ButtonGroup>
    );
  };

  const renderSummary = () => {
    const resultMap = results.reduce(
      (a, b) => {
        a[b.invoiceId] = b;
        return a;
      },
      {} as Record<string, AdproductsUserMetroidAlicornPaymentResultDto>,
    );
    return (
      <Table.Responsive>
        <Table.Root aria-label="payment summary table">
          <Table.Header>
            <Table.Row>
              <Table.HeaderCell>Invoice</Table.HeaderCell>
              <Table.HeaderCell>Account</Table.HeaderCell>
              <Table.HeaderCell>Payment Number</Table.HeaderCell>
              <Table.HeaderCell>Status</Table.HeaderCell>
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {selectedInvoices.map((invoice: AdproductsZuoraBillingInvoiceDto) => {
              const { paymentNumber, paymentSuccess } = resultMap[invoice.invoiceId] || {};
              return (
                <Table.Row key={invoice.invoiceNumber}>
                  <Table.Cell>{invoice.invoiceNumber}</Table.Cell>
                  <Table.Cell>{invoice.accountName}</Table.Cell>
                  <Table.Cell>{paymentNumber}</Table.Cell>
                  <Table.Cell>{paymentSuccess ? 'Complete' : 'Failed'}</Table.Cell>
                </Table.Row>
              );
            })}
          </Table.Body>
        </Table.Root>
      </Table.Responsive>
    );
  };

  return (
    <Modal
      open={open}
      onOpenChange={(isOpen) => {
        if (!isOpen) {
          resetState();
          close();
        }
      }}
      size="xl"
      header={
        <Heading level={6} css={{ textAlign: 'center' }}>
          {paymentState === PAYMENT_STATES.PAYMENT_COMPLETE ? 'Payment summary' : 'Pay multiple invoices'}
        </Heading>
      }
      body={paymentState === PAYMENT_STATES.PAYMENT_COMPLETE ? renderSummary() : renderMultiPaymentTable()}
      footer={renderFooter()}
      closeButton={null}
    />
  );
};

export default MultiInvoicePayModal;
